import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getProveedores, getArticulos, getMonedas, createOrdenCompra, getOrdenCompra, updateOrdenCompra, getSiguienteNumeroOC, createProveedor, createArticulo } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ClipboardList, Save, ArrowLeft, Plus, Trash2, Download, PlusCircle, Package, FileText } from 'lucide-react';
import { SearchableSelect } from '../components/ui/searchable-select';

const CONDICIONES_PAGO = [
  { value: 'CONTADO', label: 'Contado' },
  { value: 'CREDITO', label: 'Crédito' },
  { value: '30_DIAS', label: '30 días' },
  { value: '60_DIAS', label: '60 días' },
  { value: '90_DIAS', label: '90 días' },
];

const TIPOS_DOC = [
  { value: 'RUC', label: 'RUC' },
  { value: 'DNI', label: 'DNI' },
  { value: 'CE', label: 'Carnet Extranjería' },
];

const OrdenCompraFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const { empresaActual } = useApp();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [igvIncluido, setIgvIncluido] = useState(true); // IGV incluido en precios (por defecto activo)
  
  // Modal nuevo proveedor
  const [nuevoProveedorOpen, setNuevoProveedorOpen] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState({
    tipo_doc: 'RUC', nro_doc: '', razon_social: '', telefono: '', email: '', direccion: ''
  });
  const [savingProveedor, setSavingProveedor] = useState(false);
  
  // Modal nuevo artículo
  const [nuevoArticuloOpen, setNuevoArticuloOpen] = useState(false);
  const [nuevoArticulo, setNuevoArticulo] = useState({
    codigo: '', nombre: '', tipo: 'PRODUCTO', unidad: 'UND', precio_ref: ''
  });
  const [savingArticulo, setSavingArticulo] = useState(false);
  const [articuloParaLinea, setArticuloParaLinea] = useState(null); // ID de línea para asignar artículo nuevo
  
  const [formData, setFormData] = useState({
    numero: '',
    fecha: new Date().toISOString().split('T')[0],
    proveedor_id: '',
    moneda_id: '',
    condicion_pago: 'CONTADO',
    dias_credito: 0,
    direccion_entrega: '',
    observaciones: '',
    estado: 'PENDIENTE'
  });
  
  const [detalles, setDetalles] = useState([
    { id: 1, articulo_id: '', codigo: '', descripcion: '', cantidad: 1, unidad: 'UND', precio_unitario: 0 }
  ]);

  // Cargar catálogos
  const loadCatalogos = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const [provRes, artRes, monRes] = await Promise.all([
        getProveedores(empresaActual.id),
        getArticulos(empresaActual.id),
        getMonedas()
      ]);
      setProveedores(provRes.data);
      setArticulos(artRes.data);
      setMonedas(monRes.data);
      
      // Set default moneda PEN
      const pen = monRes.data.find(m => m.codigo === 'PEN');
      if (pen && !formData.moneda_id) {
        setFormData(prev => ({ ...prev, moneda_id: pen.id.toString() }));
      }
      
      // Obtener siguiente número si es nuevo
      if (!isEditing) {
        const numRes = await getSiguienteNumeroOC(empresaActual.id);
        setFormData(prev => ({ ...prev, numero: numRes.data.siguiente }));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, isEditing]);

  // Cargar orden existente
  const loadOrden = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getOrdenCompra(id);
      const orden = res.data;
      setFormData({
        numero: orden.numero,
        fecha: orden.fecha,
        proveedor_id: orden.proveedor_id?.toString() || '',
        moneda_id: orden.moneda_id?.toString() || '',
        condicion_pago: orden.condicion_pago,
        dias_credito: orden.dias_credito || 0,
        direccion_entrega: orden.direccion_entrega || '',
        observaciones: orden.observaciones || '',
        estado: orden.estado
      });
      setDetalles(orden.detalles.map((d, i) => ({
        id: i + 1,
        articulo_id: d.articulo_id?.toString() || '',
        codigo: d.codigo || '',
        descripcion: d.descripcion,
        cantidad: d.cantidad,
        unidad: d.unidad,
        precio_unitario: d.precio_unitario
      })));
    } catch (error) {
      toast.error('Error al cargar orden');
      navigate('/finanzas/ordenes-compra');
    }
  }, [id, navigate]);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    if (isEditing) loadOrden();
  }, [isEditing, loadOrden]);

  // Agregar línea
  const addLinea = () => {
    setDetalles(prev => [...prev, {
      id: Math.max(...prev.map(d => d.id)) + 1,
      articulo_id: '',
      codigo: '',
      descripcion: '',
      cantidad: 1,
      unidad: 'UND',
      precio_unitario: 0
    }]);
  };

  // Eliminar línea
  const removeLinea = (lineaId) => {
    if (detalles.length <= 1) return;
    setDetalles(prev => prev.filter(d => d.id !== lineaId));
  };

  // Actualizar línea
  const updateLinea = (lineaId, field, value) => {
    setDetalles(prev => prev.map(d => {
      if (d.id !== lineaId) return d;
      
      // Si selecciona artículo, auto-llenar datos
      if (field === 'articulo_id' && value) {
        const art = articulos.find(a => a.id.toString() === value);
        if (art) {
          return {
            ...d,
            articulo_id: value,
            codigo: art.codigo || '',
            descripcion: art.nombre,
            unidad: art.unidad || 'UND',
            precio_unitario: art.precio_ref || 0
          };
        }
      }
      
      return { ...d, [field]: value };
    }));
  };

  // Calcular totales según si IGV está incluido o no
  // NO redondear - usar todos los decimales exactos ingresados
  const totalBruto = detalles.reduce((sum, d) => sum + (d.cantidad * d.precio_unitario), 0);
  
  // Si IGV incluido: el total ES el total bruto (ya incluye IGV)
  // Si IGV no incluido: agregar IGV al total
  const total = igvIncluido ? totalBruto : totalBruto * 1.18;
  const subtotal = igvIncluido ? totalBruto / 1.18 : totalBruto;
  const igv = igvIncluido ? (totalBruto - subtotal) : (subtotal * 0.18);

  // Función para guardar nuevo proveedor
  const handleGuardarProveedor = async () => {
    if (!nuevoProveedor.nro_doc || !nuevoProveedor.razon_social) {
      toast.error('Complete RUC/DNI y Razón Social');
      return;
    }
    setSavingProveedor(true);
    try {
      const res = await createProveedor({ ...nuevoProveedor, empresa_id: empresaActual.id });
      toast.success('Proveedor creado');
      setNuevoProveedorOpen(false);
      // Recargar proveedores y seleccionar el nuevo
      const provRes = await getProveedores(empresaActual.id);
      setProveedores(provRes.data);
      setFormData(prev => ({ ...prev, proveedor_id: res.data.id.toString() }));
      setNuevoProveedor({ tipo_doc: 'RUC', nro_doc: '', razon_social: '', telefono: '', email: '', direccion: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear proveedor');
    } finally {
      setSavingProveedor(false);
    }
  };

  // Función para abrir modal de nuevo artículo (opcionalmente para una línea específica)
  const handleOpenNuevoArticulo = (lineaId = null) => {
    setArticuloParaLinea(lineaId);
    setNuevoArticulo({ codigo: '', nombre: '', tipo: 'PRODUCTO', unidad: 'UND', precio_ref: '' });
    setNuevoArticuloOpen(true);
  };

  // Función para guardar nuevo artículo
  const handleGuardarArticulo = async () => {
    if (!nuevoArticulo.nombre) {
      toast.error('El nombre del artículo es requerido');
      return;
    }
    setSavingArticulo(true);
    try {
      const payload = {
        empresa_id: empresaActual.id,
        codigo: nuevoArticulo.codigo || null,
        nombre: nuevoArticulo.nombre,
        tipo: nuevoArticulo.tipo,
        unidad: nuevoArticulo.unidad || 'UND',
        precio_ref: nuevoArticulo.precio_ref ? parseFloat(nuevoArticulo.precio_ref) : null,
        categoria_id: null,
        linea_negocio_id_def: null,
        activo: true
      };
      const res = await createArticulo(payload);
      toast.success('Artículo creado');
      setNuevoArticuloOpen(false);
      
      // Recargar artículos
      const artRes = await getArticulos(empresaActual.id);
      setArticulos(artRes.data);
      
      // Si se creó para una línea específica, asignarlo automáticamente
      if (articuloParaLinea && res.data?.id) {
        const nuevoArt = artRes.data.find(a => a.id === res.data.id);
        if (nuevoArt) {
          setDetalles(prev => prev.map(d => {
            if (d.id !== articuloParaLinea) return d;
            return {
              ...d,
              articulo_id: nuevoArt.id.toString(),
              codigo: nuevoArt.codigo || '',
              descripcion: nuevoArt.nombre,
              unidad: nuevoArt.unidad || 'UND',
              precio_unitario: nuevoArt.precio_ref || 0
            };
          }));
        }
      }
      
      setNuevoArticulo({ codigo: '', nombre: '', tipo: 'PRODUCTO', unidad: 'UND', precio_ref: '' });
      setArticuloParaLinea(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear artículo');
    } finally {
      setSavingArticulo(false);
    }
  };

  // Guardar
  const handleSubmit = async () => {
    // Validaciones
    if (!formData.numero || !formData.fecha || !formData.proveedor_id || !formData.moneda_id) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    
    const detallesValidos = detalles.filter(d => d.descripcion && d.cantidad > 0 && d.precio_unitario > 0);
    if (detallesValidos.length === 0) {
      toast.error('Agregue al menos un artículo con descripción, cantidad y precio');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        empresa_id: empresaActual.id,
        numero: formData.numero,
        fecha: formData.fecha,
        proveedor_id: parseInt(formData.proveedor_id),
        moneda_id: parseInt(formData.moneda_id),
        condicion_pago: formData.condicion_pago,
        dias_credito: parseInt(formData.dias_credito) || 0,
        direccion_entrega: formData.direccion_entrega || null,
        observaciones: formData.observaciones || null,
        igv_incluido: igvIncluido,  // Enviar flag al backend
        detalles: detallesValidos.map(d => ({
          articulo_id: d.articulo_id ? parseInt(d.articulo_id) : null,
          codigo: d.codigo || null,
          descripcion: d.descripcion,
          cantidad: parseFloat(d.cantidad),
          unidad: d.unidad,
          precio_unitario: parseFloat(d.precio_unitario)
        }))
      };

      if (isEditing) {
        await updateOrdenCompra(id, { ...payload, estado: formData.estado });
        toast.success('Orden actualizada');
      } else {
        await createOrdenCompra(payload);
        toast.success('Orden creada');
      }
      navigate('/finanzas/ordenes-compra');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Descargar PDF
  const handleDownloadPDF = async () => {
    if (!id) return;
    try {
      const response = await api.get(`/ordenes-compra/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `OC_${formData.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  // Cambiar estado
  const handleCambiarEstado = async (nuevoEstado) => {
    if (!id) return;
    try {
      await updateOrdenCompra(id, { estado: nuevoEstado });
      setFormData(prev => ({ ...prev, estado: nuevoEstado }));
      toast.success(`Estado cambiado a ${nuevoEstado}`);
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  // Convertir a factura borrador
  const [converting, setConverting] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  
  const handleConvertirAFactura = async () => {
    setConverting(true);
    try {
      const response = await api.post(`/ordenes-compra/${id}/convertir-factura`);
      toast.success('Factura borrador creada exitosamente');
      setFormData(prev => ({ ...prev, estado: 'COMPLETADA' }));
      setShowConvertDialog(false);
      // Redirigir a la factura creada
      navigate(`/finanzas/compras/${response.data.compra_id}/editar`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al convertir a factura');
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Cargando...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/finanzas/ordenes-compra')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-indigo-600" />
              {isEditing ? `Orden ${formData.numero}` : 'Nueva Orden de Compra'}
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing && (
            <>
              <Button variant="outline" onClick={handleDownloadPDF}>
                <Download className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
              {formData.estado === 'PENDIENTE' && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowConvertDialog(true)} 
                    disabled={converting}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {converting ? 'Convirtiendo...' : 'Convertir a Factura'}
                  </Button>
                  <Button variant="outline" onClick={() => handleCambiarEstado('COMPLETADA')} className="text-emerald-600 border-emerald-300">
                    Marcar Completada
                  </Button>
                </>
              )}
              {formData.estado === 'COMPLETADA' && (
                <Button variant="outline" onClick={() => handleCambiarEstado('PENDIENTE')} className="text-yellow-600 border-yellow-300">
                  Reabrir
                </Button>
              )}
            </>
          )}
          <Button onClick={handleSubmit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* Formulario */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Datos principales */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>N° Orden *</Label>
                <Input
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  placeholder="OC-0001"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Moneda *</Label>
                <Select value={formData.moneda_id} onValueChange={(v) => setFormData({ ...formData, moneda_id: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {monedas.map(m => (
                      <SelectItem key={m.id} value={m.id.toString()}>{m.codigo} - {m.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Proveedor *</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex-1">
                  <SearchableSelect
                    options={proveedores.map(p => ({ value: p.id.toString(), label: `${p.razon_social} (${p.nro_doc})` }))}
                    value={formData.proveedor_id}
                    onValueChange={(v) => setFormData({ ...formData, proveedor_id: v })}
                    placeholder="Seleccionar proveedor"
                    searchPlaceholder="Buscar proveedor..."
                  />
                </div>
                <Button type="button" variant="outline" onClick={() => setNuevoProveedorOpen(true)} title="Crear nuevo proveedor">
                  <PlusCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Condición de Pago</Label>
                <Select value={formData.condicion_pago} onValueChange={(v) => setFormData({ ...formData, condicion_pago: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDICIONES_PAGO.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Días Crédito</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.dias_credito}
                  onChange={(e) => setFormData({ ...formData, dias_credito: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Dirección de Entrega</Label>
              <Input
                value={formData.direccion_entrega}
                onChange={(e) => setFormData({ ...formData, direccion_entrega: e.target.value })}
                placeholder="Dirección donde entregar la mercadería"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Observaciones</Label>
              <Textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                placeholder="Notas adicionales para el proveedor..."
                className="mt-1"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detalle de artículos y Resumen */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Detalle de artículos - 3 columnas */}
        <Card className="lg:col-span-3">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900">Detalle de Artículos</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                  <Switch checked={igvIncluido} onCheckedChange={setIgvIncluido} id="igv-switch" />
                  <Label htmlFor="igv-switch" className="text-sm cursor-pointer">IGV incluido</Label>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleOpenNuevoArticulo(null)} title="Crear nuevo artículo">
                  <Package className="w-4 h-4 mr-1" />
                  Nuevo Artículo
                </Button>
                <Button variant="outline" size="sm" onClick={addLinea}>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Línea
                </Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-10">#</TableHead>
                    <TableHead className="w-48">Artículo</TableHead>
                    <TableHead className="w-24">Código</TableHead>
                    <TableHead>Descripción *</TableHead>
                    <TableHead className="w-20">Cant. *</TableHead>
                    <TableHead className="w-20">Unidad</TableHead>
                    <TableHead className="w-28">P. Unit. *</TableHead>
                    <TableHead className="w-28 text-right">Subtotal</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detalles.map((linea, index) => (
                    <TableRow key={linea.id}>
                      <TableCell className="text-gray-500">{index + 1}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <div className="flex-1">
                            <SearchableSelect
                              options={articulos.map(a => ({ value: a.id.toString(), label: `${a.codigo ? `[${a.codigo}] ` : ''}${a.nombre}` }))}
                              value={linea.articulo_id}
                              onValueChange={(v) => updateLinea(linea.id, 'articulo_id', v)}
                              placeholder="Buscar artículo..."
                              searchPlaceholder="Buscar por nombre o código..."
                              className="h-9"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenNuevoArticulo(linea.id)}
                            title="Crear nuevo artículo"
                            className="h-9 w-9 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          >
                            <PlusCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={linea.codigo}
                          onChange={(e) => updateLinea(linea.id, 'codigo', e.target.value)}
                          className="h-9"
                          placeholder="Código"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={linea.descripcion}
                          onChange={(e) => updateLinea(linea.id, 'descripcion', e.target.value)}
                          className="h-9"
                          placeholder="Descripción del artículo"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={linea.cantidad}
                          onChange={(e) => updateLinea(linea.id, 'cantidad', parseFloat(e.target.value) || 0)}
                          className="h-9 text-right"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={linea.unidad}
                          onChange={(e) => updateLinea(linea.id, 'unidad', e.target.value)}
                          className="h-9"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={linea.precio_unitario}
                          onChange={(e) => updateLinea(linea.id, 'precio_unitario', parseFloat(e.target.value) || 0)}
                          className="h-9 text-right"
                        />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {(linea.cantidad * linea.precio_unitario).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLinea(linea.id)}
                          disabled={detalles.length <= 1}
                          className="h-8 w-8 p-0 text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Resumen - 1 columna */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Resumen</h3>
            
            {isEditing && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Estado:</span>
                <span className={`px-2 py-1 rounded text-sm font-medium ${
                  formData.estado === 'COMPLETADA' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {formData.estado}
                </span>
              </div>
            )}

            {igvIncluido && (
              <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                Precios con IGV incluido
              </div>
            )}
            
            <div className="space-y-2 pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal:</span>
                <span>{monedas.find(m => m.id.toString() === formData.moneda_id)?.codigo || 'PEN'} {subtotal.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">IGV (18%):</span>
                <span>{monedas.find(m => m.id.toString() === formData.moneda_id)?.codigo || 'PEN'} {igv.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>Total:</span>
                <span className="text-indigo-600">{monedas.find(m => m.id.toString() === formData.moneda_id)?.codigo || 'PEN'} {total.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>

            <div className="pt-4 text-xs text-gray-400">
              {detalles.filter(d => d.descripcion).length} artículo(s)
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal Nuevo Proveedor */}
      <Dialog open={nuevoProveedorOpen} onOpenChange={setNuevoProveedorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-indigo-600" />
              Nuevo Proveedor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo Doc.</Label>
                <Select value={nuevoProveedor.tipo_doc} onValueChange={(v) => setNuevoProveedor({...nuevoProveedor, tipo_doc: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOC.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>N° Documento *</Label>
                <Input
                  value={nuevoProveedor.nro_doc}
                  onChange={(e) => setNuevoProveedor({...nuevoProveedor, nro_doc: e.target.value})}
                  placeholder={nuevoProveedor.tipo_doc === 'RUC' ? '20XXXXXXXXX' : '12345678'}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Razón Social / Nombre *</Label>
              <Input
                value={nuevoProveedor.razon_social}
                onChange={(e) => setNuevoProveedor({...nuevoProveedor, razon_social: e.target.value})}
                placeholder="Nombre del proveedor"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={nuevoProveedor.telefono}
                  onChange={(e) => setNuevoProveedor({...nuevoProveedor, telefono: e.target.value})}
                  placeholder="987654321"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={nuevoProveedor.email}
                  onChange={(e) => setNuevoProveedor({...nuevoProveedor, email: e.target.value})}
                  placeholder="proveedor@email.com"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input
                value={nuevoProveedor.direccion}
                onChange={(e) => setNuevoProveedor({...nuevoProveedor, direccion: e.target.value})}
                placeholder="Dirección del proveedor"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuevoProveedorOpen(false)}>Cancelar</Button>
            <Button onClick={handleGuardarProveedor} disabled={savingProveedor} className="bg-indigo-600 hover:bg-indigo-700">
              {savingProveedor ? 'Guardando...' : 'Guardar Proveedor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nuevo Artículo */}
      <Dialog open={nuevoArticuloOpen} onOpenChange={setNuevoArticuloOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Nuevo Artículo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código</Label>
                <Input
                  value={nuevoArticulo.codigo}
                  onChange={(e) => setNuevoArticulo({...nuevoArticulo, codigo: e.target.value})}
                  placeholder="ART-001"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={nuevoArticulo.tipo} onValueChange={(v) => setNuevoArticulo({...nuevoArticulo, tipo: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRODUCTO">Producto</SelectItem>
                    <SelectItem value="SERVICIO">Servicio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                value={nuevoArticulo.nombre}
                onChange={(e) => setNuevoArticulo({...nuevoArticulo, nombre: e.target.value})}
                placeholder="Nombre del artículo o servicio"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unidad</Label>
                <Input
                  value={nuevoArticulo.unidad}
                  onChange={(e) => setNuevoArticulo({...nuevoArticulo, unidad: e.target.value})}
                  placeholder="UND, KG, M, etc."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Precio Referencia</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={nuevoArticulo.precio_ref}
                  onChange={(e) => setNuevoArticulo({...nuevoArticulo, precio_ref: e.target.value})}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
            </div>
            {articuloParaLinea && (
              <div className="text-sm text-emerald-600 bg-emerald-50 p-2 rounded">
                El artículo se asignará automáticamente a la línea seleccionada.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNuevoArticuloOpen(false); setArticuloParaLinea(null); }}>Cancelar</Button>
            <Button onClick={handleGuardarArticulo} disabled={savingArticulo} className="bg-emerald-600 hover:bg-emerald-700">
              {savingArticulo ? 'Guardando...' : 'Guardar Artículo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación para convertir a factura */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convertir a Factura Borrador</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              ¿Estás seguro de convertir esta orden de compra en una factura borrador?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              La factura se creará con los mismos datos de la orden (proveedor, artículos, precios, IGV) y podrás editarla antes de confirmarla.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)} disabled={converting}>
              Cancelar
            </Button>
            <Button onClick={handleConvertirAFactura} disabled={converting} className="bg-green-600 hover:bg-green-700">
              <FileText className="w-4 h-4 mr-2" />
              {converting ? 'Convirtiendo...' : 'Sí, Convertir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdenCompraFormPage;
